/**
 * Slider Mappings Configuration
 * 
 * Defines all slider configurations and their mappings to render parameters.
 */

/**
 * Slider configurations
 * Each slider has:
 * - name: Internal parameter name
 * - label: Display label in UI
 * - min: Minimum slider value (UI display)
 * - max: Maximum slider value (UI display)
 * - default: Default value (in UI range)
 * - step: Slider step increment
 */
export const SLIDER_CONFIGS = [
  { name: 'style', label: 'Strength', min: -4, max: 4, default: 0.0, step: 0.1 },
  { name: 'fidelity', label: 'Fidelity', min: -4, max: 4, default: 4.0, step: 0.1 },
  { name: 're_light', label: 'Re-Light', min: 0, max: 4, default: 0.0, step: 0.1 },
  { name: 're_sky', label: 'Re-Sky', min: 0, max: 4, default: 0.0, step: 0.1 },
  { name: 'blend_original', label: 'Blend Original', min: 0, max: 4, default: 0.0, step: 0.1 },
  { name: 'fog', label: 'Fog', min: 0, max: 4, default: 0.0, step: 0.1 },
  { name: 'light_rays', label: 'Light Rays', min: 0, max: 4, default: 0.0, step: 0.1 },
  { name: 'atmosphere', label: 'Atmosphere', min: 0, max: 4, default: 0.0, step: 0.1 },
  { name: 'epicness', label: 'Epic-ness', min: 0, max: 4, default: 0.0, step: 0.1 }
];

/**
 * Map a value from one range to another
 */
export function mapRange(value, inMin, inMax, outMin, outMax) {
  return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

/**
 * Get slider configuration by name
 */
export function getSliderConfig(name) {
  return SLIDER_CONFIGS.find(config => config.name === name);
}

/**
 * Custom mapping function for strength slider
 * Maps slider value [-4.0, 4.0] to render value [0.0, 4.0] with center at 1.0
 * 
 * @param {number} sliderValue - Slider value from -4.0 to 4.0
 * @returns {number} Mapped render value
 */
function mapStrengthValue(sliderValue) {
  if (sliderValue <= 0.0) {
    // Map [-4.0, 0.0] to [0.0, 1.0]
    return (sliderValue + 4.0) / 4.0;
  } else {
    // Map [0.0, 4.0] to [1.0, 4.0]
    return 1.0 + (sliderValue * 0.75);
  }
}

/**
 * Map slider value to render parameter
 * 
 * Each slider may have a custom mapping to its corresponding render parameter.
 * Some sliders map to different parameter names or ranges.
 * 
 * @param {string} sliderName - Name of the slider
 * @param {number} sliderValue - Current slider value (UI range)
 * @param {object} config - Slider configuration
 * @returns {object} Object with parameter name and mapped value
 */
export function mapSliderToRenderParam(sliderName, sliderValue, config) {
  switch (sliderName) {
    case 'style':
      // Strength slider: UI shows -4 to 4, maps to 0.0 to 4.0 with piecewise mapping
      // -4.0 to 0.0 → 0.0 to 1.0
      // 0.0 to 4.0 → 1.0 to 4.0
      return {
        paramName: 'style_strength',
        value: mapStrengthValue(sliderValue)
      };
    
    case 'fidelity':
      // Fidelity slider (-4 to 4) maps to controlScale (0.0 to 1.0)
      return {
        paramName: 'controlScale',
        value: mapRange(sliderValue, config.min, config.max, 0.0, 1.0)
      };
    
    default:
      // Other sliders map to 0.0 to 1.0 range
      return {
        paramName: sliderName,
        value: mapRange(sliderValue, config.min, config.max, 0.0, 1.0)
      };
  }
}

/**
 * Get all render parameters from current slider values
 * 
 * @param {object} photo - Photo object with current slider values
 * @param {string} activeSliderName - Currently active slider (optional)
 * @param {number} activeSliderValue - Value to use for active slider (optional)
 * @param {number} inferenceSteps - Number of inference steps
 * @param {number} seed - Random seed
 * @param {number} sampleEvery - Sample every N steps (default 2)
 * @returns {object} Render parameters object
 */
export function getRenderParams(photo, activeSliderName = null, activeSliderValue = null, inferenceSteps = 6, seed = 42, sampleEvery = 2) {
  const params = {
    numInferenceSteps: inferenceSteps,
    style_strength: 1.0, // Default (maps to UI value of 0.0)
    controlScale: 1.0,
    seed: seed,
    sampleEvery: sampleEvery,
    jpgQuality: 85
  };

  // Get all current slider values and map to render parameters
  SLIDER_CONFIGS.forEach(config => {
    const sliderValue = config.name === activeSliderName 
      ? activeSliderValue 
      : (photo[config.name] !== undefined ? photo[config.name] : config.default);

    const { paramName, value } = mapSliderToRenderParam(config.name, sliderValue, config);
    params[paramName] = value;
  });

  return params;
}

